import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Building2, X } from 'lucide-react';
import { ArWeeklyActionRecord, ArWeeklyMetricRecord, ArWeeklyRevenueContributionRecord } from '../../../api/ledgerViewsApi';

interface ArWeeklySummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    metric: ArWeeklyMetricRecord | null;
    actions: ArWeeklyActionRecord[];
    contributions: ArWeeklyRevenueContributionRecord[];
    onOpenArInvoice?: (invoiceId: string) => void;
    onOpenArCustomer?: (customerId: string, customerName?: string) => void;
}

const formatCurrencyCompact = (value?: number | null): string => (
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
    }).format(Number(value || 0))
);

const extractActionContext = (action: ArWeeklyActionRecord): { customerName: string; invoiceNumber: string } => {
    const title = String(action.title || '').trim();
    const description = String((action as ArWeeklyActionRecord & { description?: string }).description || action.details || '').trim();

    let customerName = String(action.customer_name || '').trim();
    let invoiceNumber = String(action.invoice_number || '').trim();

    if (!customerName) {
        const concentrationMatch = title.match(/^Customer concentration risk:\s+(.+)$/i);
        if (concentrationMatch) {
            customerName = concentrationMatch[1].trim();
        }
    }

    if (!invoiceNumber) {
        const titleInvoiceMatch = title.match(/\bfor\s+([A-Za-z0-9-]+)\s*$/i) || title.match(/\bInvoice\s+([A-Za-z0-9-]+)\b/i);
        if (titleInvoiceMatch) {
            invoiceNumber = titleInvoiceMatch[1].trim();
        }
    }

    if (!customerName || !invoiceNumber) {
        const descInvoiceMatch = description.match(/^(.+?)\s+invoice\s+([A-Za-z0-9-]+)\b/i);
        if (descInvoiceMatch) {
            if (!customerName) customerName = descInvoiceMatch[1].trim();
            if (!invoiceNumber) invoiceNumber = descInvoiceMatch[2].trim();
        }
    }

    return { customerName, invoiceNumber };
};

const formatActionLabel = (action: ArWeeklyActionRecord): string => {
    const title = String(action.title || '').trim();
    const { customerName, invoiceNumber } = extractActionContext(action);
    const contextLabel = [customerName, invoiceNumber].filter(Boolean).join(' · ');

    if (action.action_type === 'review_partial_payment') {
        return contextLabel ? `Partial payment detected for ${contextLabel}` : (title || 'Partial payment detected');
    }
    if (action.action_type === 'follow_up_invoice') {
        return contextLabel ? `Follow up on ${contextLabel}` : (title || 'Follow up on invoice');
    }
    if (action.action_type === 'adjust_cash_expectation') {
        return contextLabel ? `Expected receipt missed for ${contextLabel}` : (title || 'Expected receipt missed');
    }
    if (action.action_type === 'review_non_revenue_inflow' || action.action_type === 'review_customer_concentration' || action.action_type === 'escalate_top_overdue_balances') {
        return customerName ? `Customer concentration risk: ${customerName}` : (title || 'Customer concentration risk');
    }

    if (title && contextLabel && !title.includes(contextLabel)) {
        return `${title}: ${contextLabel}`;
    }

    return title
        || String((action as ArWeeklyActionRecord & { description?: string }).description || action.details || '').trim()
        || contextLabel
        || action.action_type
        || 'Review AR item';
};

const formatContributionReason = (value?: string | null): string => {
    const raw = String(value || '').trim();
    if (!raw) return 'Included in AR revenue';
    if (raw === 'receipt_not_resolved' || raw === 'receipt_review_status_pending_qbo_confirmation') {
        return 'Pending QBO confirmation';
    }
    if (raw === 'receipt_classification_excluded:generic_bank_deposit') {
        return 'Pending QBO confirmation';
    }
    return raw
        .split(':')
        .join(' · ')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatContributionLabel = (item: ArWeeklyRevenueContributionRecord): string => {
    const customerName = String(item.customer_name || '').trim();
    const invoiceNumber = String(item.invoice_number || '').trim();
    const outcome = String(item.receipt_outcome || item.allocation_type || '').trim().replace(/_/g, ' ');
    const context = [customerName, invoiceNumber].filter(Boolean).join(' · ');
    return [context || 'AR receipt', outcome].filter(Boolean).join(' · ');
};

const dedupeContributions = (items: ArWeeklyRevenueContributionRecord[]): ArWeeklyRevenueContributionRecord[] => {
    const seen = new Set<string>();
    return items.filter((item) => {
        const key = String(item.contribution_id || `${item.cash_application_id || ''}|${item.invoice_id || ''}|${item.allocated_amount || ''}`);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
};

const ArWeeklySummaryModal: React.FC<ArWeeklySummaryModalProps> = ({
    isOpen,
    onClose,
    metric,
    actions,
    contributions,
    onOpenArInvoice,
    onOpenArCustomer,
}) => {
    useEffect(() => {
        if (!isOpen) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen]);

    const handleActionClick = (action: ArWeeklyActionRecord) => {
        const invoiceId = String(action.invoice_id || '').trim();
        const customerId = String(action.customer_id || '').trim();
        const customerName = String(action.customer_name || '').trim() || undefined;

        if (invoiceId && onOpenArInvoice) {
            onClose();
            onOpenArInvoice(invoiceId);
            return;
        }
        if (customerId && onOpenArCustomer) {
            onClose();
            onOpenArCustomer(customerId, customerName);
        }
    };

    const isActionClickable = (action: ArWeeklyActionRecord) => {
        const invoiceId = String(action.invoice_id || '').trim();
        const customerId = String(action.customer_id || '').trim();
        return (!!invoiceId && !!onOpenArInvoice) || (!!customerId && !!onOpenArCustomer);
    };

    if (!isOpen) return null;

    const dedupedContributions = dedupeContributions(contributions);
    const includedContributions = dedupedContributions.filter((item) => item.included_in_weekly_revenue).slice(0, 5);
    const excludedContributions = dedupedContributions.filter((item) => !item.included_in_weekly_revenue).slice(0, 5);

    return createPortal(
        <div className="fixed inset-0 z-[65] overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center px-4 py-8">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-70" onClick={onClose} />
                <div data-tutorial="ledger-ar-weekly-summary-modal" className="relative w-full max-w-3xl rounded-lg bg-white shadow-xl">
                    <div className="border-b border-gray-200 px-5 py-4">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3">
                                <div className="rounded-md bg-indigo-50 p-2 text-indigo-700">
                                    <Building2 className="h-4 w-4" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">AR Weekly Summary</h3>
                                    <p className="mt-0.5 text-sm text-gray-500">
                                        {metric?.week_start?.slice(0, 10) && metric?.week_end?.slice(0, 10)
                                            ? `${metric.week_start.slice(0, 10)} to ${metric.week_end.slice(0, 10)}`
                                            : 'Latest AR operating summary'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
                        {!metric ? (
                            <div className="py-10 text-sm text-gray-500">No AR weekly summary available.</div>
                        ) : (
                            <div className="space-y-6">
                                <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-gray-500">Collected</div>
                                        <div className="mt-1 text-lg font-semibold text-gray-900">{formatCurrencyCompact(metric.actual_revenue_linked_cash)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-gray-500">Expected</div>
                                        <div className="mt-1 text-lg font-semibold text-gray-900">{formatCurrencyCompact(metric.expected_cash_due)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-gray-500">Missed Expected</div>
                                        <div className="mt-1 text-lg font-semibold text-gray-900">{formatCurrencyCompact(metric.missed_expected_cash_amount)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-gray-500">Open Overdue AR</div>
                                        <div className="mt-1 text-lg font-semibold text-gray-900">{formatCurrencyCompact(metric.total_overdue_amount)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-gray-500">Newly Overdue</div>
                                        <div className="mt-1 text-lg font-semibold text-gray-900">{formatCurrencyCompact(metric.new_overdue_amount)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-gray-500">Unapplied Cash</div>
                                        <div className="mt-1 text-lg font-semibold text-gray-900">{formatCurrencyCompact(metric.unapplied_cash_amount)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-gray-500">Partial Payment Risk</div>
                                        <div className="mt-1 text-lg font-semibold text-gray-900">{formatCurrencyCompact(metric.partial_payment_amount)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-wide text-gray-500">Open AR</div>
                                        <div className="mt-1 text-lg font-semibold text-gray-900">{formatCurrencyCompact(metric.open_ar)}</div>
                                    </div>
                                </div>

                                <div className="border-t border-emerald-100 pt-4">
                                    <div className="text-sm font-semibold text-gray-900">
                                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-100">
                                            Revenue Attribution
                                        </span>
                                    </div>
                                    <div className="mt-3 space-y-6">
                                        <div>
                                            <div className="text-xs uppercase tracking-wide text-emerald-700">Included In AR Revenue</div>
                                            {includedContributions.length ? (
                                                <div className="mt-2 divide-y divide-gray-100 text-sm text-gray-700">
                                                    {includedContributions.map((item) => (
                                                        <div key={item.contribution_id || `${item.cash_application_id}-${item.invoice_id}`} className="py-3 first:pt-0 last:pb-0">
                                                            <div className="flex items-center justify-between gap-3">
                                                                <div className="min-w-0 truncate font-medium text-gray-900">{formatContributionLabel(item)}</div>
                                                                <div className="shrink-0 text-sm font-medium text-gray-900">{formatCurrencyCompact(item.allocated_amount)}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="mt-2 text-sm text-gray-500">No included AR revenue contributions for this week.</div>
                                            )}
                                        </div>
                                        <div className="border-t border-amber-100 pt-4">
                                            <div className="text-xs uppercase tracking-wide text-amber-700">Excluded From AR Revenue</div>
                                            {excludedContributions.length ? (
                                                <div className="mt-2 divide-y divide-gray-100 text-sm text-gray-700">
                                                    {excludedContributions.map((item) => (
                                                        <div key={item.contribution_id || `${item.cash_application_id}-${item.invoice_id}`} className="py-3 first:pt-0 last:pb-0">
                                                            <div className="flex items-center justify-between gap-3">
                                                                <div className="min-w-0">
                                                                    <div className="truncate font-medium text-gray-900">{formatContributionLabel(item)}</div>
                                                                    <div className="truncate text-xs text-gray-500">{formatContributionReason(item.exclusion_reason || 'Excluded')}</div>
                                                                </div>
                                                                <div className="shrink-0 text-sm font-medium text-gray-900">{formatCurrencyCompact(item.allocated_amount)}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="mt-2 text-sm text-gray-500">No excluded AR revenue contributions for this week.</div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="border-t border-sky-100 pt-4">
                                    <div className="text-sm font-semibold text-gray-900">
                                        <span className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 ring-1 ring-sky-100">
                                            This Week&apos;s AR Actions
                                        </span>
                                    </div>
                                    {actions.length ? (
                                        <div className="mt-3 divide-y divide-gray-100 text-sm text-gray-700">
                                            {actions.slice(0, 6).map((action, index) => (
                                                <button
                                                    key={`${action.action_type || 'action'}-${index}`}
                                                    type="button"
                                                    disabled={!isActionClickable(action)}
                                                    onClick={() => handleActionClick(action)}
                                                    className={`block w-full px-0 py-3 text-left ${
                                                        isActionClickable(action)
                                                            ? 'hover:text-gray-900 text-gray-700'
                                                            : 'text-gray-700'
                                                    }`}
                                                >
                                                    {index + 1}. {formatActionLabel(action)}
                                                    {typeof action.amount === 'number' ? ` · ${formatCurrencyCompact(action.amount)}` : ''}
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="mt-3 text-sm text-gray-500">No AR actions for this week.</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default ArWeeklySummaryModal;
